import {
  DEFAULT_BOOKING_WINDOW_DAYS,
  DEFAULT_NOTICE_MINUTES,
  DEFAULT_SLOT_MINUTES,
  type ServiceSchedule,
  type SlotOption,
  type ScheduleService,
  type WeeklyRule,
} from "@/lib/scheduling/types";

/** Asia/Seoul 오프셋 — 한국은 DST 없음 */
const SEOUL_OFFSET_MS = 9 * 60 * 60 * 1000;

export function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

export function minutesToTime(min: number): string {
  const h = String(Math.floor(min / 60)).padStart(2, "0");
  const m = String(min % 60).padStart(2, "0");
  return `${h}:${m}`;
}

/** 브라우저/서버 공통: 현재 시각을 Asia/Seoul 달력 기준으로 해석 */
export function nowInSeoul(now = new Date()): Date {
  return new Date(now.getTime() + SEOUL_OFFSET_MS);
}

export function formatDateKeySeoul(d: Date): string {
  const seoul = new Date(d.getTime() + SEOUL_OFFSET_MS);
  const y = seoul.getUTCFullYear();
  const m = String(seoul.getUTCMonth() + 1).padStart(2, "0");
  const day = String(seoul.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function seoulDayOfWeek(dateKey: string): number {
  const [y, m, d] = dateKey.split("-").map(Number);
  // 정오 UTC+9 = 03:00 UTC → 요일 안정
  const utc = Date.UTC(y, m - 1, d, 3, 0, 0);
  return new Date(utc).getUTCDay();
}

export function seoulDateTimeToUtcIso(dateKey: string, time: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  const minutes = timeToMinutes(time);
  const h = Math.floor(minutes / 60);
  const min = minutes % 60;
  const utcMs = Date.UTC(y, m - 1, d, h, min, 0) - SEOUL_OFFSET_MS;
  return new Date(utcMs).toISOString();
}

export function addMinutesToTime(time: string, add: number): string {
  return minutesToTime(timeToMinutes(time) + add);
}

export function lockKey(instructorId: string, date: string, time: string) {
  return `${instructorId}_${date}_${time.replace(":", "")}`;
}

/** 소요시간에 필요한 30분 그리드 잠금 키 목록 */
export function buildLockKeys(
  instructorId: string,
  date: string,
  startTime: string,
  durationMinutes: number,
  gridMinutes = DEFAULT_SLOT_MINUTES
): string[] {
  const keys: string[] = [];
  const start = timeToMinutes(startTime);
  for (let min = start; min < start + durationMinutes; min += gridMinutes) {
    keys.push(lockKey(instructorId, date, minutesToTime(min)));
  }
  return keys;
}

export function emptyServiceSchedule(
  overrides?: Partial<ServiceSchedule>
): ServiceSchedule {
  return {
    slotMinutes: DEFAULT_SLOT_MINUTES,
    weeklyRules: [],
    blockedDates: [],
    minimumNoticeMinutes: DEFAULT_NOTICE_MINUTES,
    bookingWindowDays: DEFAULT_BOOKING_WINDOW_DAYS,
    ...overrides,
  };
}

/**
 * 서비스 스케줄 + 점유 잠금으로 예약 가능 슬롯 생성.
 * durationMinutes 동안 연속 그리드가 모두 비어 있어야 함.
 */
export function buildServiceSlots(params: {
  instructorId: string;
  instructorName?: string;
  service: ScheduleService;
  schedule: ServiceSchedule;
  durationMinutes: number;
  takenLockKeys: Set<string>;
  now?: Date;
  /** 특정 날짜만 (YYYY-MM-DD) */
  onlyDate?: string;
}): Map<string, SlotOption[]> {
  const {
    instructorId,
    instructorName,
    service,
    schedule,
    durationMinutes,
    takenLockKeys,
    onlyDate,
  } = params;
  const now = params.now ?? new Date();
  const seoulNow = nowInSeoul(now);
  const todayKey = formatDateKeySeoul(now);
  const noticeMs = (schedule.minimumNoticeMinutes || DEFAULT_NOTICE_MINUTES) * 60_000;
  const minStartMs = now.getTime() + noticeMs;
  const windowDays = schedule.bookingWindowDays || DEFAULT_BOOKING_WINDOW_DAYS;
  const grid = schedule.slotMinutes || DEFAULT_SLOT_MINUTES;
  const slotsByDate = new Map<string, SlotOption[]>();

  for (let offset = 0; offset <= windowDays; offset++) {
    const probe = new Date(
      Date.UTC(
        seoulNow.getUTCFullYear(),
        seoulNow.getUTCMonth(),
        seoulNow.getUTCDate() + offset,
        12,
        0,
        0
      )
    );
    // probe is noon UTC of seoul calendar day — convert to dateKey via UTC parts
    const dateKey = `${probe.getUTCFullYear()}-${String(probe.getUTCMonth() + 1).padStart(2, "0")}-${String(probe.getUTCDate()).padStart(2, "0")}`;
    if (onlyDate && dateKey !== onlyDate) continue;
    if (schedule.blockedDates.includes(dateKey)) continue;

    const dayOfWeek = seoulDayOfWeek(dateKey);
    const options: SlotOption[] = [];
    const rules = schedule.weeklyRules.filter(r => r.day === dayOfWeek);

    for (const rule of rules) {
      const startMin = timeToMinutes(rule.start);
      const endMin = timeToMinutes(rule.end);

      for (let min = startMin; min + durationMinutes <= endMin; min += grid) {
        const time = minutesToTime(min);
        const needed = buildLockKeys(
          instructorId,
          dateKey,
          time,
          durationMinutes,
          grid
        );
        if (needed.some(k => takenLockKeys.has(k))) continue;

        const startIso = seoulDateTimeToUtcIso(dateKey, time);
        if (new Date(startIso).getTime() < minStartMs) continue;
        // 과거 날짜 스킵
        if (dateKey < todayKey) continue;

        options.push({
          date: dateKey,
          time,
          instructorId,
          instructorName,
          service,
          durationMinutes,
        });
      }
    }

    if (options.length > 0) {
      options.sort((a, b) => a.time.localeCompare(b.time));
      slotsByDate.set(dateKey, options);
    }
  }

  return slotsByDate;
}

export function isSlotInsideSchedule(
  schedule: ServiceSchedule,
  date: string,
  time: string,
  durationMinutes: number
): boolean {
  if (schedule.blockedDates.includes(date)) return false;
  const day = seoulDayOfWeek(date);
  const start = timeToMinutes(time);
  const end = start + durationMinutes;
  return schedule.weeklyRules.some(rule => {
    if (rule.day !== day) return false;
    return start >= timeToMinutes(rule.start) && end <= timeToMinutes(rule.end);
  });
}

export function normalizeWeeklyRules(rules: unknown): WeeklyRule[] {
  if (!Array.isArray(rules)) return [];
  return rules
    .map(item => {
      if (!item || typeof item !== "object") return null;
      const r = item as Record<string, unknown>;
      const day = Number(r.day);
      const start = String(r.start || "");
      const end = String(r.end || "");
      if (!Number.isInteger(day) || day < 0 || day > 6) return null;
      if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return null;
      if (start >= end) return null;
      return { day, start, end } satisfies WeeklyRule;
    })
    .filter((r): r is WeeklyRule => r !== null);
}
